/* eslint-disable no-process-env */
import { useEffect, useState } from 'react';
import { useMeContext } from '~/modules/me/useMeContext';
import { useIsBreakpointDown } from '~/modules/common/hooks';
import { USER_ACCESS_ROLE } from '~/modules/common/enums';

export const aptrinsic = 'aptrinsic';
const sourceUrl = 'https://web-sdk.aptrinsic.com/api/aptrinsic.js';
const MOBILE_CHANNEL = '3';

export const updateTagKeyChannel = (tagKey, channel) => {
  if (!tagKey) return tagKey;
  const parts = tagKey.split('-');

  if (parts.length > 2) {
    parts[2] = channel + parts[2].substring(1);

    return parts.join('-');
  }

  return tagKey;
};

export const getProductCode = gainsightTagKey => {
  if (!gainsightTagKey || !gainsightTagKey.includes('-')) return '';

  return gainsightTagKey.split('-')[1];
};

export const getUserRoles = userAccessRoles =>
  userAccessRoles?.length ? userAccessRoles.join(',') : null;

export const getUpdmAttributeBucket01 = userAccessRoles => {
  if (!userAccessRoles?.length) return null;

  const isReportUser = userAccessRoles.includes(USER_ACCESS_ROLE.REPORT_USER);
  const isProjectResource = userAccessRoles.includes(
    USER_ACCESS_ROLE.PROJECT_RESOURCE
  );
  const isEndUserOnly = isProjectResource && userAccessRoles.length === 1;

  return `isEndUserOnly:${isEndUserOnly},isReportUser:${isReportUser},isProjectResource:${isProjectResource}`;
};

export const getIagUserAttributes = userAccessRoles => {
  const role = getUserRoles(userAccessRoles);
  const uPDMAttributeBucket01 = getUpdmAttributeBucket01(userAccessRoles);

  return {
    ...(role && { role }),
    ...(uPDMAttributeBucket01 && {
      uPDM_AttributeBucket01: uPDMAttributeBucket01
    })
  };
};

export const getIagAccountAttributes = ({ licensingSummary }) => ({
  ...(licensingSummary?.enabledUserCount != null && {
    allocatedUsers: licensingSummary.enabledUserCount
  }),
  ...(licensingSummary?.products?.length && {
    productSubscriptions: licensingSummary.products
      .map(p => p.displayText)
      .join(',')
  })
});

const initGainsight = (tagKey, setScriptLoaded) => {
  window[aptrinsic] =
    window[aptrinsic] ||
    ((...args) => {
      (window[aptrinsic].q = window[aptrinsic].q || []).push(args);
    });
  window[aptrinsic].p = tagKey;
  const script = document.createElement('script');

  script.async = true;
  script.src = `${sourceUrl}?a=${tagKey}`;
  script.onload = () => {
    setScriptLoaded && setScriptLoaded(true);
  };
  script.onerror = () => {
    setScriptLoaded && setScriptLoaded(false);
  };
  const c = document.getElementsByTagName('script')[0];

  c?.parentNode?.insertBefore(script, c);
};

export const useInitGainsightPX = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const isMobile = useIsBreakpointDown('sm');
  const {
    id,
    firstName,
    lastName,
    emailAddress,
    companyKey,
    tenantSlug,
    gainsightTagKey,
    isGainsightDisabled,
    userAccessRoles,
    licensingSummary
  } = useMeContext();

  useEffect(() => {
    if (!id || !scriptLoaded || !window[aptrinsic]) return;
    const productCode = getProductCode(gainsightTagKey);

    window[aptrinsic](
      'identify',
      {
        id: `rpcn_${id}`,
        email: emailAddress,
        firstName,
        lastName,
        ...getIagUserAttributes(userAccessRoles)
      },
      {
        id: `rpcn_${tenantSlug}`,
        name: companyKey,
        productVersion: 'Polaris',
        productCode,
        ...getIagAccountAttributes({ licensingSummary })
      }
    );

    window[aptrinsic]('set', 'globalContext', { productVersion: 'Polaris' });
  }, [
    id,
    tenantSlug,
    scriptLoaded,
    gainsightTagKey,
    emailAddress,
    firstName,
    lastName,
    companyKey,
    userAccessRoles,
    licensingSummary
  ]);

  useEffect(() => {
    if (!gainsightTagKey || scriptLoaded || isGainsightDisabled) return;

    const tagKey = isMobile
      ? updateTagKeyChannel(gainsightTagKey, MOBILE_CHANNEL)
      : gainsightTagKey;

    initGainsight(tagKey, setScriptLoaded);
  }, [isMobile, gainsightTagKey, scriptLoaded, isGainsightDisabled]);
};
